import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, NavLink, useNavigate } from 'react-router-dom';
import { axios } from '../hooks/useAuth';
import '../styles/admin.css';

/* ─── Sub-pages ──────────────────────────────────────────────── */

function AdminDashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get('/api/admin/dashboard').then(r => setStats(r.data.stats)).catch(() => {});
  }, []);

  const cards = stats ? [
    { icon: '👥', label: 'Total Users', value: stats.users },
    { icon: '🎵', label: 'Active Songs', value: stats.songs },
    { icon: '🎙️', label: 'Compilations', value: stats.compilations },
    { icon: '✅', label: 'Active Compilations', value: stats.activeCompilations },
  ] : [];

  return (
    <div>
      <h2 className="admin-section-title">Dashboard</h2>
      <div className="admin-stats-grid">
        {stats ? cards.map((c, i) => (
          <div key={i} className="stat-card">
            <div className="stat-icon">{c.icon}</div>
            <div className="stat-value">{c.value?.toLocaleString()}</div>
            <div className="stat-label">{c.label}</div>
          </div>
        )) : (
          <p className="text-muted">Loading stats...</p>
        )}
      </div>
    </div>
  );
}

function AdminSongs() {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: '', artist: '', genre: '', difficulty: 'medium', previewStart: 0 });
  const [files, setFiles] = useState({ audio: null, coverArt: null });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    axios.get('/api/songs?limit=100&sortBy=createdAt')
      .then(r => setSongs(r.data.songs || []))
      .finally(() => setLoading(false));
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!files.audio) { setMsg('Audio file required'); return; }
    setSaving(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append('audio', files.audio);
    if (files.coverArt) fd.append('coverArt', files.coverArt);
    try {
      const res = await axios.post('/api/songs', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSongs(prev => [res.data.song, ...prev]);
      setShowAdd(false);
      setMsg('✅ Song added!');
    } catch (err) {
      setMsg(err.response?.data?.error || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this song?')) return;
    await axios.delete(`/api/songs/${id}`);
    setSongs(prev => prev.filter(s => s.id !== id));
  };

  return (
    <div>
      <div className="admin-section-header">
        <h2 className="admin-section-title">Songs</h2>
        <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(!showAdd)}>
          {showAdd ? '✕ Cancel' : '+ Add Song'}
        </button>
      </div>

      {msg && <div className="alert alert-success">{msg}</div>}

      {showAdd && (
        <form className="card admin-form" onSubmit={handleAdd}>
          <h3 style={{ marginBottom: 16 }}>Add New Song</h3>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Title *</label>
              <input className="form-input" required value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Artist *</label>
              <input className="form-input" required value={form.artist}
                onChange={e => setForm({ ...form, artist: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Genre</label>
              <input className="form-input" value={form.genre}
                onChange={e => setForm({ ...form, genre: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Difficulty</label>
              <select className="form-input" value={form.difficulty}
                onChange={e => setForm({ ...form, difficulty: e.target.value })}>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Preview Start (seconds)</label>
              <input type="number" className="form-input" min={0} step={1}
                value={form.previewStart}
                onChange={e => setForm({ ...form, previewStart: parseFloat(e.target.value) })} />
            </div>
            <div className="form-group">
              <label className="form-label">BPM</label>
              <input type="number" className="form-input" value={form.bpm || ''}
                onChange={e => setForm({ ...form, bpm: e.target.value })} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Audio File (MP3/WAV) *</label>
            <input type="file" className="form-input" accept="audio/*"
              onChange={e => setFiles({ ...files, audio: e.target.files[0] })} required />
          </div>
          <div className="form-group">
            <label className="form-label">Cover Art (optional)</label>
            <input type="file" className="form-input" accept="image/*"
              onChange={e => setFiles({ ...files, coverArt: e.target.files[0] })} />
          </div>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? '⏳ Uploading...' : '💾 Save Song'}
          </button>
        </form>
      )}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr><th>Title</th><th>Artist</th><th>Genre</th><th>Diff</th><th>Views</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-muted text-center" style={{ padding: 24 }}>Loading...</td></tr>
            ) : songs.map(s => (
              <tr key={s.id}>
                <td className="font-bold">{s.title}</td>
                <td className="text-secondary">{s.artist}</td>
                <td>{s.genre || '—'}</td>
                <td><span className={`badge diff-${s.difficulty}`}>{s.difficulty}</span></td>
                <td className="font-mono">{s.viewCount}</td>
                <td>
                  <span className={`badge ${s.isActive ? 'badge-green' : 'badge-fuchsia'}`}>
                    {s.isActive ? 'Active' : 'Hidden'}
                  </span>
                </td>
                <td>
                  <div className="flex gap-2">
                    <Link to={`/admin/lyrics/${s.id}`} className="btn btn-secondary btn-sm">Lyrics</Link>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(s.id)}>Remove</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminLyricEditor() {
  const [songId, setSongId] = useState('');
  const [songs, setSongs] = useState([]);
  const [lyrics, setLyrics] = useState([]);
  const [rawText, setRawText] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    axios.get('/api/songs?limit=100').then(r => setSongs(r.data.songs || []));
  }, []);

  const loadLyrics = async (id) => {
    setSongId(id);
    if (!id) return;
    const res = await axios.get(`/api/songs/${id}/lyrics`);
    const lyr = res.data.lyrics || [];
    setLyrics(lyr);
    // Convert to LRC-like text for editing
    setRawText(lyr.map(l => `[${formatLRC(l.startTime)}] ${l.text}`).join('\n'));
  };

  const formatLRC = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, '0');
    const sec = (s % 60).toFixed(2).padStart(5, '0');
    return `${m}:${sec}`;
  };

  const parseLRC = (text) => {
    return text.split('\n')
      .filter(l => l.trim())
      .map((line, i) => {
        const match = line.match(/^\[(\d+):(\d+\.\d+)\]\s*(.*)/);
        if (match) {
          const mins = parseFloat(match[1]);
          const secs = parseFloat(match[2]);
          return { startTime: mins * 60 + secs, text: match[3], lineIndex: i };
        }
        return null;
      })
      .filter(Boolean);
  };

  const handleSave = async () => {
    if (!songId) return;
    setSaving(true);
    try {
      const parsed = parseLRC(rawText);
      await axios.put(`/api/songs/${songId}/lyrics`, { lyrics: parsed });
      setMsg(`✅ Saved ${parsed.length} lyric lines`);
    } catch (err) {
      setMsg('❌ Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h2 className="admin-section-title">Lyric Editor</h2>
      <div className="card" style={{ padding: 24, marginBottom: 20 }}>
        <div className="form-group">
          <label className="form-label">Select Song</label>
          <select className="form-input" value={songId} onChange={e => loadLyrics(e.target.value)}>
            <option value="">-- Choose a song --</option>
            {songs.map(s => <option key={s.id} value={s.id}>{s.title} — {s.artist}</option>)}
          </select>
        </div>

        {songId && (
          <>
            <p className="text-sm text-muted" style={{ marginBottom: 12 }}>
              Format: <code className="font-mono" style={{ background: 'var(--bg-elevated)', padding: '2px 6px', borderRadius: 4 }}>[MM:SS.cs] Lyric text here</code>
              &nbsp;— One line per entry
            </p>
            <textarea
              className="form-input lyric-editor-textarea"
              value={rawText}
              onChange={e => setRawText(e.target.value)}
              rows={20}
              placeholder={`[00:05.00] First lyric line\n[00:10.50] Second lyric line\n[00:15.25] And so on...`}
            />
            {msg && <div className="alert alert-success" style={{ marginTop: 12 }}>{msg}</div>}
            <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={handleSave} disabled={saving}>
              {saving ? '⏳ Saving...' : '💾 Save Lyrics'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    axios.get('/api/admin/users?limit=100')
      .then(r => setUsers(r.data.users || []))
      .finally(() => setLoading(false));
  }, []);

  const handleToggle = async (user, field) => {
    const updated = { ...user, [field]: !user[field] };
    await axios.put(`/api/admin/users/${user.id}`, { [field]: updated[field] });
    setUsers(prev => prev.map(u => u.id === user.id ? updated : u));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this user?')) return;
    await axios.delete(`/api/admin/users/${id}`);
    setUsers(prev => prev.map(u => u.id === id ? { ...u, isActive: false } : u));
  };

  const filtered = users.filter(u =>
    !search || u.username?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <h2 className="admin-section-title">Users</h2>
      <div className="form-group" style={{ marginBottom: 16 }}>
        <input className="form-input" placeholder="Search by username or email..."
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr><th>User</th><th>Email</th><th>Role</th><th>Verified</th><th>Active</th><th>Joined</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-muted text-center" style={{ padding: 24 }}>Loading...</td></tr>
            ) : filtered.map(u => (
              <tr key={u.id}>
                <td>
                  <span style={{ marginRight: 6 }}>{u.emoji}</span>
                  <strong>{u.displayName || u.username}</strong>
                  <span className="text-muted text-sm" style={{ display: 'block' }}>@{u.username}</span>
                </td>
                <td className="text-secondary text-sm">{u.email}</td>
                <td><span className={`badge ${u.role === 'admin' ? 'badge-gold' : 'badge-purple'}`}>{u.role}</span></td>
                <td>
                  <span className={`badge ${u.isVerified ? 'badge-green' : ''}`}>
                    {u.isVerified ? '✅' : '⏳'}
                  </span>
                </td>
                <td>
                  <button className={`badge ${u.isActive ? 'badge-green' : 'badge-fuchsia'}`}
                    onClick={() => handleToggle(u, 'isActive')} style={{ cursor: 'pointer', border: 'none', background: 'none' }}>
                    {u.isActive ? 'Active' : 'Disabled'}
                  </button>
                </td>
                <td className="text-muted text-sm">{new Date(u.createdAt).toLocaleDateString()}</td>
                <td>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(u.id)}>Disable</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminEmail() {
  const [users, setUsers] = useState([]);
  const [to, setTo] = useState('all');
  const [customTo, setCustomTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    axios.get('/api/admin/users?limit=200').then(r => setUsers(r.data.users || []));
  }, []);

  const handleSend = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      let recipients;
      if (to === 'all') {
        recipients = users.map(u => u.email);
      } else if (to === 'custom') {
        recipients = customTo.split(',').map(e => e.trim()).filter(Boolean);
      } else {
        recipients = [to];
      }
      await axios.post('/api/admin/email', { recipients, subject, body });
      setMsg(`✅ Sent to ${recipients.length} recipient(s)`);
      setSubject(''); setBody('');
    } catch (err) {
      setMsg('❌ Send failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <h2 className="admin-section-title">Send Email</h2>
      <div className="card" style={{ padding: 28 }}>
        <form onSubmit={handleSend}>
          <div className="form-group">
            <label className="form-label">Recipients</label>
            <select className="form-input" value={to} onChange={e => setTo(e.target.value)}>
              <option value="all">All Users ({users.length})</option>
              <option value="custom">Custom Emails</option>
              {users.map(u => <option key={u.id} value={u.email}>{u.displayName} &lt;{u.email}&gt;</option>)}
            </select>
          </div>
          {to === 'custom' && (
            <div className="form-group">
              <label className="form-label">Email Addresses (comma-separated)</label>
              <input className="form-input" value={customTo} onChange={e => setCustomTo(e.target.value)}
                placeholder="a@example.com, b@example.com" />
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Subject *</label>
            <input className="form-input" required value={subject} onChange={e => setSubject(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Message (HTML supported) *</label>
            <textarea className="form-input" rows={8} required value={body}
              onChange={e => setBody(e.target.value)}
              placeholder="<p>Hello singers!</p>" />
          </div>
          {msg && <div className={`alert ${msg.startsWith('✅') ? 'alert-success' : 'alert-error'}`}>{msg}</div>}
          <button type="submit" className="btn btn-primary" disabled={sending}>
            {sending ? '⏳ Sending...' : '📧 Send Email'}
          </button>
        </form>
      </div>
    </div>
  );
}

function AdminSettings() {
  const [settings, setSettings] = useState([]);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const DEFAULT_SETTINGS = [
    { key: 'default_max_tracks', value: '4', description: 'Default max vocal tracks per compilation', category: 'compilations' },
    { key: 'compilation_ttl_days', value: '30', description: 'Days before auto-purging compilations (0 = never)', category: 'compilations' },
    { key: 'compilation_max_views', value: '1000', description: 'View count before auto-purging (0 = never)', category: 'compilations' },
  ];

  useEffect(() => {
    axios.get('/api/admin/settings').then(r => {
      const loaded = r.data.settings || [];
      const map = {};
      loaded.forEach(s => { map[s.key] = s.value; });
      // Fill defaults
      DEFAULT_SETTINGS.forEach(d => { if (!(d.key in map)) map[d.key] = d.value; });
      setValues(map);
      setSettings(loaded.length ? loaded : DEFAULT_SETTINGS);
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = Object.entries(values).map(([key, value]) => ({ key, value }));
      await axios.put('/api/admin/settings', { settings: payload });
      setMsg('✅ Settings saved');
    } catch {
      setMsg('❌ Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h2 className="admin-section-title">Settings</h2>
      <div className="card" style={{ padding: 28 }}>
        {DEFAULT_SETTINGS.map(s => (
          <div key={s.key} className="form-group">
            <label className="form-label">{s.key.replace(/_/g, ' ').toUpperCase()}</label>
            <input
              type="number"
              className="form-input"
              value={values[s.key] ?? s.value}
              onChange={e => setValues({ ...values, [s.key]: e.target.value })}
              style={{ maxWidth: 200 }}
            />
            <div className="text-sm text-muted" style={{ marginTop: 4 }}>{s.description}</div>
          </div>
        ))}
        {msg && <div className={`alert ${msg.startsWith('✅') ? 'alert-success' : 'alert-error'}`}>{msg}</div>}
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? '⏳ Saving...' : '💾 Save Settings'}
        </button>
      </div>
    </div>
  );
}

function AdminCompilations() {
  const [compilations, setCompilations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get('/api/admin/compilations')
      .then(r => setCompilations(r.data.compilations || []))
      .finally(() => setLoading(false));
  }, []);

  const handlePurge = async (id) => {
    if (!window.confirm('Permanently purge this compilation?')) return;
    await axios.delete(`/api/admin/compilations/${id}`);
    setCompilations(prev => prev.map(c => c.id === id ? { ...c, status: 'purged' } : c));
  };

  return (
    <div>
      <h2 className="admin-section-title">Compilations</h2>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr><th>Title</th><th>Song</th><th>Creator</th><th>Tracks</th><th>Views</th><th>Status</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-muted text-center" style={{ padding: 24 }}>Loading...</td></tr>
            ) : compilations.map(c => (
              <tr key={c.id}>
                <td className="font-bold truncate" style={{ maxWidth: 180 }}>{c.title}</td>
                <td className="text-secondary text-sm">{c.song?.title}</td>
                <td>{c.creator?.username}</td>
                <td className="font-mono">{c.trackCount}/{c.maxTracks}</td>
                <td className="font-mono">{c.viewCount}</td>
                <td>
                  <span className={`badge ${c.status === 'active' ? 'badge-green' : 'badge-fuchsia'}`}>
                    {c.status}
                  </span>
                </td>
                <td>
                  {c.status === 'active' && (
                    <button className="btn btn-danger btn-sm" onClick={() => handlePurge(c.id)}>Purge</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─── Main Admin Layout ──────────────────────────────────────── */
export default function AdminPage() {
  const NAV = [
    { to: '/admin', label: '📊 Dashboard', end: true },
    { to: '/admin/songs', label: '🎵 Songs' },
    { to: '/admin/lyrics', label: '📝 Lyrics' },
    { to: '/admin/users', label: '👥 Users' },
    { to: '/admin/compilations', label: '🎙️ Compilations' },
    { to: '/admin/email', label: '📧 Email' },
    { to: '/admin/settings', label: '⚙️ Settings' },
  ];

  return (
    <div className="admin-layout page-enter">
      <aside className="admin-sidebar">
        <div className="admin-sidebar-header">
          <span className="gradient-text" style={{ fontWeight: 900 }}>Admin Panel</span>
        </div>
        <nav className="admin-nav">
          {NAV.map(n => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) => `admin-nav-link ${isActive ? 'active' : ''}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
      </aside>

      <main className="admin-main">
        <Routes>
          <Route index element={<AdminDashboard />} />
          <Route path="songs" element={<AdminSongs />} />
          <Route path="lyrics" element={<AdminLyricEditor />} />
          <Route path="lyrics/:songId" element={<AdminLyricEditor />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="compilations" element={<AdminCompilations />} />
          <Route path="email" element={<AdminEmail />} />
          <Route path="settings" element={<AdminSettings />} />
        </Routes>
      </main>
    </div>
  );
}
